import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import TicketsButton from '../components/TicketsButton';
import { setCheckoutOverlay, startCheckout, ticketPickerOpened } from '../actions/ui';
import { getSeriesConfig } from 'checkout/selectors/ui';
import {
  setEventTimes,
  setSelectedDate,
  setSelectedChildEvent,
  setSelectedEventTimeObject,
  selectChildEventAndContinue,
  getEventTimes, getEventTimesForDates
} from '../views/series/actions/event-series';
import { isValidDateObject } from '../../helpers/time';
import { removeListingItem } from '../../listing/helpers/storage';
import { getFormattedEventDates, getSeriesOption} from '../views/series/selectors/eventSeries';
import { isKioskMode } from '../selectors/ui';
import { getMainEvent, isEventDatePast } from '../selectors/event';
import { fetchEvent } from '../actions/event';
import Translation from '@s/Translation';
import { getTimestampAndRemoveFromUrl } from '../helpers/url';
import { isSameDay } from '../../../../shared/util/dates';
import { withRouterCompat } from '@h/withRouterCompat';
import { navigate } from '@h/navigationService';

class TicketsButtonContainer extends Component {
  state = {
    loading: false,
  };

  componentDidMount() {
    const timestamp = getTimestampAndRemoveFromUrl('date');

    if (timestamp) {
      if (this.props.isRecurring) {
        if (this.props.calendarType === 'list') {
          this.props.getEventTimesForDates(this.props.eventDates).then(() => this._startCheckout());
        } else {
          const date = new Date(timestamp);
          const isDateIsAvailable = this.props.eventDates.some((availableDate) => isSameDay(availableDate, date));

          if (isDateIsAvailable) {
            this.props.getEventTimes(this.props.eventId, date, null, null, true).then(() => this._startCheckout());
          }
        }
      } else {
        this._startCheckout();
      }
    }
  }

  render() {
    if (this.props.maintenanceMode) {
      return null;
    }

    if(this.props.listingSoldOut === true) {
      if (this.props.uiConfig?.listings?.useCustomSoldOutText && typeof this.props.uiConfig.listings.soldOutText !== "undefined" && this.props.uiConfig.listings.soldOutText !== "") {
        return <>{this.props.uiConfig.listings.soldOutText}</>;
      }

      const isLiveCondition = !this.props.isLive ? '' : <Translation>{"checkout__sold_out"}</Translation>;
      const isKioskModeCondition = this.props.isKioskMode ? <Translation>{"checkout__kiosk_sold_out"}</Translation> : isLiveCondition;

      return typeof this.props.eventEnded === 'boolean' && this.props.eventEnded ? <Translation>{"checkout__event_ended"}</Translation> : isKioskModeCondition;
    }

    return (
      <TicketsButton
        uiConfig={this.props.uiConfig}
        priceLevels={this.props.priceLevels}
        onClick={this.startCheckout}
        coupon={this.props.coupon}
        hasPresale={this.props.hasPresale}
        isOnsale={this.props.isOnsale}
        isCanceled={this.props.isCanceled}
        title={this.props.title}
        type={this.props.type}
        loading={this.state.loading}
        isRecurring={this.props.isRecurring}
        maintenanceModeLoaded={this.props.maintenanceModeLoaded}
        disabled={this.props.taggableCouponLoading}
        invitationCouponApplied={this.props.inviteCouponApplied}
        isPaused={this.props.isPaused}
      >
        {this.props.children}
      </TicketsButton>
    );
  }

  startCheckout = () => {
    if (this.props.isMembershipAvailable && !this.props.isMembershipSkipped && !this.props.hasMembershipInstance
      && (this.props.isMembershipRequired || !this.props.membershipSkipLogin)) {
      this.props.setCheckoutOverlay(true);
      navigate("/member-login", { state: { onLogin: this.startCheckout } });
      return;
    }

    //in kiosk mode, refresh local data before transitioning to the next step
    if (this.props.isKioskMode && !(this.props.showAllDayEvents)) {
      this.setState({ loading: true });
      this.props.fetchEvent().finally(() => {
        this.setState({ loading: false });
        //if option to open to nearest available event time enabled for kiosk,
        //  1. open to nearest time if the next time is not sold out
        //  2. if the next time is sold out open to the kiosk time picker
        //  3. if the day is sold out, open to series date picker
        //
        //Note: this code should likely be revisted when inventory service is up and running.
        if (this.props.isRecurring && this.props.openToNextAvailable) {
          let hasOnsaleEventToday = false;
          if (this.props.eventsToday && this.props.eventsToday.length) {
            this.props.eventsToday.map((eventTime) => {
              if (eventTime.eventStatus === 'on_sale') {
                hasOnsaleEventToday = true;
              }
            });

            if (!hasOnsaleEventToday) {
              this._startCheckout();
            } else {
              const now = new Date();

              //sort by time ascending
              const todaysEvents = this.props.eventsToday.sort((a, b) => {
                const aDate = new Date(a.time);
                const bDate = new Date(b.time);
                if (!isValidDateObject(aDate) || !isValidDateObject(bDate)) {
                  return 1;
                }
                return aDate.getTime() - bDate.getTime();
              });

              for (let i = 0; i < todaysEvents.length; i++) {
                const event = todaysEvents[i];
                const eventTime = new Date(event.time);

                if (isValidDateObject(eventTime) && eventTime.getTime() > now.getTime()) {
                  //if closest event is not on sale, open time picker
                  if (event.eventStatus !== 'on_sale') {
                    this.props.setSelectedDate(now).then(() => {
                      this.props.setCheckoutOverlay(true);
                      this.props.navigate('/kiosk-time');
                    });
                  } else {
                    //clear previously populated data
                    this.props.setEventTimes([]);
                    removeListingItem(this.props.listingSlug, 'selectedEventId');

                    this.props.setSelectedDate(eventTime).then(() => {
                      this.selectEvent(event);
                    });
                  }

                  break;
                }
              }
            }
          } else {
            this._startCheckout();
          }
        } else {
          this._startCheckout();
        }
      });
    } else {
      this._startCheckout();
    }
  };

  _startCheckout = () => {
    this.props.startCheckout(this.props.showCheckoutCallback);
  }

  selectEvent = (event) => {
    this.props.setSelectedEventTimeObject(event);
    this.props.setSelectedChildEvent(event.id);
    this.props.selectChildEventAndContinue(event.id);
  }
}

const mapStateToProps = (state) => {
  const theEvent = getMainEvent(state)?.event ?? {};

  return {
    eventEnded: isEventDatePast(theEvent.salesCloseDate),
    listingSlug: state.ui.listingSlug,
    uiConfig: state.ui.uiConfig,
    priceLevels: state.priceLevel,
    coupon: state.coupon,
    hasPresale: theEvent.hasPresale,
    isOnsale: theEvent.isOnsale,
    isCanceled: theEvent.isCanceled,
    isPaused: theEvent.isPaused,
    hasPlAccess: theEvent.hasPlAccess,
    isRecurring: theEvent.isRecurring,
    eventDates: getFormattedEventDates(state),
    listingSoldOut: state.ui.listingSoldOut,
    eventId: theEvent.event_id,
    apiUrl: state.ui.apiUrl,
    openToNextAvailable: getSeriesOption("openToNextAvailable", false)(state) ? true : false,
    isKioskMode: isKioskMode(state),
    eventsToday: state.eventSeries.eventsToday,
    maintenanceMode: state.ui.maintenanceMode,
    maintenanceModeLoaded: state.ui.maintenanceModeLoaded,
    taggableCouponLoading: state.couponTag.taggableCouponLoading,
    inviteCouponApplied: state.couponTag.appliedTaggableInvitationCode || state.reservation.hasInviteCoupon,
    isLive: theEvent.isLive,
    calendarType: getSeriesConfig(state).calendarType,
    showAllDayEvents: getSeriesOption("showAllDayEvents", false)(state) ? true : false,
    isMembershipAvailable: !!state.ui?.uiConfig?.membershipFormConfig?.membership_available,
    isMembershipRequired: !!state.ui?.uiConfig?.membershipFormConfig?.membership_required,
    membershipSkipLogin: !!state.ui?.uiConfig?.membershipFormConfig?.skip_login,
    isMembershipSkipped: !!state.ui?.skipMembership,
    hasMembershipInstance: !!state.reservation?.hasMembershipInstance,
  }
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      ticketPickerOpened,
      setEventTimes,
      setSelectedDate,
      setSelectedChildEvent,
      selectChildEventAndContinue,
      fetchEvent,
      setSelectedEventTimeObject,
      getEventTimes,
      getEventTimesForDates,
      startCheckout,
      setCheckoutOverlay,
    },
    dispatch,
  );
};

TicketsButtonContainer.propTypes = {
  showCheckoutCallback: PropTypes.func,
  title: PropTypes.string,
  type: PropTypes.string,
};

TicketsButtonContainer.defaultProps = {
  title: '',
  type: '',
};

export default withRouterCompat(connect(mapStateToProps, mapDispatchToProps)(TicketsButtonContainer));
